import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../../context/DriverAuthContext';
import useWs from '../../../hooks/useWs';
import '../../DriverApp.css';
import SAR from '../../../components/SAR';

const Money = ({ n }) => n != null ? <>{parseFloat(n).toFixed(2)}{' '}<SAR className="h-[0.85em] w-auto" /></> : <>-</>;
const fmtMin   = m => m != null ? (m < 60 ? `${Math.round(m)} د` : `${(m/60).toFixed(1)} س`) : '-';
const fmtKm    = k => k != null ? `${parseFloat(k).toFixed(1)} كم` : '-';

const AVAIL_LABELS = { online: 'متاح', offline: 'غير متاح', on_break: 'استراحة', busy: 'مشغول' };
const AVAIL_CLASS  = { online: 'online', offline: 'offline', on_break: 'break', busy: 'busy' };

// ── Bid Sheet (bottom drawer) ──────────────────────────────────────────────
function BidSheet({ sheet, onClose, onSubmit, submitting }) {
  const { options, offer } = sheet;
  const [selectedShip, setSelectedShip] = useState(null);
  const ships = options?.ships || [];
  const chosen = ships.find(s => s.ship_id === selectedShip)
    ?? (options?.auto_select ? ships[0] : null);

  if (!sheet.open) return null;

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:1000,
      background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'flex-end',
    }} onClick={onClose}>
      <div
        style={{
          width:'100%', background:'#fff', borderRadius:'20px 20px 0 0',
          padding:'20px 16px 32px', maxHeight:'85vh', overflowY:'auto',
          color:'#1a1a1a',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
          <h3 style={{margin:0, fontSize:18, fontWeight:800}}>💰 تقديم عرض</h3>
          <button onClick={onClose} style={{background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#888'}}>×</button>
        </div>

        {/* Order info */}
        <div style={{background:'#f7f9fb', borderRadius:12, padding:'12px 14px', marginBottom:16}}>
          <div style={{display:'flex', justifyContent:'space-between', fontSize:13, color:'#555', marginBottom:4}}>
            <span>📦 الكمية</span>
            <span style={{fontWeight:700}}>{options?.quantity_tons} طن</span>
          </div>
          <div style={{display:'flex', justifyContent:'space-between', fontSize:13, color:'#555'}}>
            <span>💧 نوع المياه</span>
            <span style={{fontWeight:700}}>{options?.water_type || '-'}</span>
          </div>
        </div>

        {/* Single ship → auto confirm */}
        {options?.auto_select && chosen && (
          <div>
            <div style={{background:'#eafbf0', borderRadius:12, padding:'14px', marginBottom:16, border:'2px solid #22c55e'}}>
              <div style={{fontWeight:700, fontSize:15, marginBottom:8, color:'#1a1a1a'}}>
                {chosen.display_name || chosen.name_ar || chosen.name || chosen.ship_code || 'شيب'}
                {' '}💧
              </div>
              {chosen.distance_km && (
                <div style={{fontSize:13, color:'#666', marginBottom:6}}>📍 {fmtKm(chosen.distance_km)} من موقع التسليم</div>
              )}
              {chosen.pricing && (
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, fontSize:13}}>
                  <div><span style={{color:'#888'}}>سعر الطن: </span><b><Money n={chosen.pricing.price_per_ton} /></b></div>
                  <div><span style={{color:'#888'}}>المجموع: </span><b><Money n={chosen.pricing.subtotal} /></b></div>
                  <div><span style={{color:'#888'}}>ضريبة: </span><b><Money n={chosen.pricing.tax_total} /></b></div>
                  <div style={{fontWeight:800, color:'#16a34a'}}>
                    <span style={{color:'#888'}}>الإجمالي: </span><Money n={chosen.pricing.total_amount} />
                  </div>
                </div>
              )}
            </div>
            <button
              className="drv-btn drv-btn--green"
              style={{width:'100%', fontSize:16, padding:'14px'}}
              disabled={submitting}
              onClick={() => onSubmit(offer.id, null)}
            >
              {submitting ? <span className="drv-spinner drv-spinner--sm"/> : '✓ إرسال العرض'}
            </button>
          </div>
        )}

        {/* Multi ship → show selector */}
        {!options?.auto_select && (
          <div>
            <div style={{fontSize:13, color:'#555', marginBottom:10, fontWeight:600}}>
              اختر الشيب الذي ستقدم العرض منه:
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:10, marginBottom:16}}>
              {ships.map(s => (
                <div
                  key={s.ship_id}
                  onClick={() => setSelectedShip(s.ship_id)}
                  style={{
                    border: selectedShip === s.ship_id ? '2px solid #16a34a' : '2px solid #e5e7eb',
                    borderRadius:12, padding:'12px 14px', cursor:'pointer',
                    background: selectedShip === s.ship_id ? '#eafbf0' : '#fafafa',
                    transition:'all .15s',
                  }}
                >
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontWeight:700, fontSize:14, color:'#1a1a1a'}}>
                        {s.display_name || s.name_ar || s.name || s.ship_code || 'شيب'}
                        {' '}💧
                      </div>
                      {s.distance_km && (
                        <div style={{fontSize:12, color:'#888', marginTop:2}}>📍 {fmtKm(s.distance_km)} من التسليم</div>
                      )}
                    </div>
                    {s.rating && (
                      <div style={{fontSize:12, color:'#f59e0b', fontWeight:700}}>⭐ {s.rating.toFixed(1)}</div>
                    )}
                  </div>
                  {s.pricing && (
                    <div style={{marginTop:8, display:'flex', gap:12, fontSize:12, color:'#555'}}>
                      <span>طن: <b><Money n={s.pricing.price_per_ton} /></b></span>
                      <span>|</span>
                      <span style={{fontWeight:800, color:'#16a34a'}}>الإجمالي: <Money n={s.pricing.total_amount} /></span>
                    </div>
                  )}
                  {!s.has_pricing && (
                    <div style={{marginTop:6, fontSize:11, color:'#ef4444'}}>⚠ لا يوجد تسعير لهذا الشيب</div>
                  )}
                </div>
              ))}
            </div>

            {/* Selected ship pricing summary */}
            {selectedShip && chosen?.pricing && (
              <div style={{background:'#f7f9fb', borderRadius:10, padding:'10px 12px', marginBottom:14, fontSize:13}}>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:4}}>
                  <div><span style={{color:'#888'}}>المجموع: </span><b><Money n={chosen.pricing.subtotal} /></b></div>
                  <div><span style={{color:'#888'}}>ضريبة: </span><b><Money n={chosen.pricing.tax_total} /></b></div>
                </div>
                <div style={{marginTop:6, fontWeight:800, color:'#16a34a', fontSize:14}}>
                  الإجمالي: <Money n={chosen.pricing.total_amount} />
                </div>
              </div>
            )}

            <button
              className="drv-btn drv-btn--green"
              style={{width:'100%', fontSize:16, padding:'14px'}}
              disabled={submitting || !selectedShip || !chosen?.has_pricing}
              onClick={() => onSubmit(offer.id, selectedShip)}
            >
              {submitting ? <span className="drv-spinner drv-spinner--sm"/> : '✓ إرسال العرض'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Toast context (local)
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type='info') => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 5000);
  };
  return { toasts, add };
}

export default function HomePage() {
  const { driver, apiCall, setDriver } = useDriverAuth();
  const nav = useNavigate();
  const { toasts, add } = useToasts();

  const [offers,      setOffers]      = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [toggling,    setToggling]    = useState(false);
  const [timers,      setTimers]      = useState({});
  const [stats,       setStats]       = useState(null);

  // Bid sheet state
  const [bidSheet,    setBidSheet]    = useState({ open: false, offer: null, options: null });
  const [bidLoading,  setBidLoading]  = useState(null);   // offer_id being fetched
  const [bidSubmit,   setBidSubmit]   = useState(false);  // submit in progress

  const sseRef  = useRef(null);
  const timerRef = useRef({});

  // ── Load stats ─────────────────────────────────────────────────────────────
  useEffect(() => {
    apiCall('/orders/history?limit=5')
      .then(d => {
        const completed = d.orders.filter(o => o.status === 'completed');
        const earn = completed.reduce((s, o) => s + (o.total || 0), 0);
        setStats({ completed: completed.length, earnings: earn });
      }).catch(() => {});
  }, []);

  // ── SSE stream ─────────────────────────────────────────────────────────────
  const connectSSE = useCallback(() => {
    if (sseRef.current) sseRef.current.close();
    const token = localStorage.getItem('drv_token');
    const es = new EventSource(`/api/v1/driver/stream`, {});
    // Pass token via custom header not possible in EventSource — use query param approach
    // Instead we'll poll manually every 3s
    es.close(); // close immediately and use polling instead
    sseRef.current = null;
    startPolling();
  }, []);

  // ── Polling fallback (EventSource can't set headers) ──────────────────────
  const pollRef = useRef(null);
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const [offData, ordData] = await Promise.all([
          apiCall('/orders/offers'),
          apiCall('/orders/active'),
        ]);
        setOffers(prev => {
          const newIds = (offData.offers || []).map(o => o.id).join(',');
          const prevIds = prev.map(o => o.id).join(',');
          if (newIds !== prevIds && offData.offers?.length > prev.length) {
            add('طلب جديد وصلك! 🔔', 'offer');
          }
          return offData.offers || [];
        });
        setActiveOrder(ordData.active_order || null);
        // Start offer countdown timers
        (offData.offers || []).forEach(o => {
          if (o.expires_at && !timerRef.current[o.id]) {
            timerRef.current[o.id] = true;
          }
        });
      } catch {}
    };
    tick();
    pollRef.current = setInterval(tick, 5000); // fallback only — WS handles instant
  }, [apiCall]);

  useEffect(() => {
    startPolling();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [startPolling]);

  // WS: instant offer push + order status changes
  const wsToken = localStorage.getItem('drv_token') || '';
  const wsPath  = driver?.id ? `/driver/${driver.id}` : null;
  useWs(wsPath, useCallback((data) => {
    if (data.type === 'new_offer' || data.type === 'order_status') {
      startPolling(); // re-fetch immediately
    }
  }, [startPolling]), wsToken);

  // ── Offer countdown timer ──────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prev => {
        const next = { ...prev };
        offers.forEach(o => {
          if (o.expires_at) {
            const secs = Math.max(0, Math.round((new Date(o.expires_at) - Date.now()) / 1000));
            next[o.id] = secs;
          }
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [offers]);

  // ── GPS location update ────────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation || driver?.availability_status !== 'online') return;
    const watchId = navigator.geolocation.watchPosition(
      pos => {
        apiCall('/auth/location', {
          method: 'PUT',
          body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        }).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [driver?.availability_status]);

  // ── Toggle availability ────────────────────────────────────────────────────
  const toggleAvail = async () => {
    const next = driver?.availability_status === 'online' ? 'offline' : 'online';
    setToggling(true);
    try {
      await apiCall('/auth/availability', { method: 'PUT', body: JSON.stringify({ status: next }) });
      setDriver(d => ({ ...d, availability_status: next }));
      add(next === 'online' ? 'أنت الآن متاح للطلبات ✅' : 'تم تغيير حالتك لغير متاح', next === 'online' ? 'success' : 'info');
    } catch (e) { add(e.message || 'خطأ', 'error'); }
    finally     { setToggling(false); }
  };

  // ── Open bid sheet (fetch options first) ──────────────────────────────────
  const openBidSheet = async (offer) => {
    setBidLoading(offer.id);
    try {
      const options = await apiCall(`/orders/offers/${offer.id}/bid-options`);
      if (options.already_submitted) {
        add('أرسلت عرضك على هذا الطلب مسبقاً', 'info');
        return;
      }
      setBidSheet({ open: true, offer, options });
    } catch (e) {
      add(e.message || 'تعذّر تحميل خيارات العرض', 'error');
    } finally {
      setBidLoading(null);
    }
  };

  const closeBidSheet = () => setBidSheet({ open: false, offer: null, options: null });

  // ── Submit bid ─────────────────────────────────────────────────────────────
  const submitBid = async (offerId, shipId) => {
    setBidSubmit(true);
    try {
      const res = await apiCall(`/orders/offers/${offerId}/submit-bid`, {
        method: 'POST',
        body: JSON.stringify({ ship_id: shipId || null }),
      });
      if (res.require_ship_selection) {
        add('يرجى اختيار شيب من القائمة', 'info');
        return;
      }
      add('✅ تم إرسال عرضك بنجاح — في انتظار اختيار العميل', 'success');
      closeBidSheet();
      setOffers(p => p.filter(o => o.id !== offerId));
    } catch (e) {
      add(e.message || 'فشل إرسال العرض', 'error');
    } finally {
      setBidSubmit(false);
    }
  };

  // ── Reject offer ───────────────────────────────────────────────────────────
  const rejectOffer = async (offerId) => {
    try {
      await apiCall(`/orders/offers/${offerId}/reject`, { method: 'POST' });
      setOffers(p => p.filter(o => o.id !== offerId));
    } catch {}
  };

  const avail = driver?.availability_status || 'offline';
  const isOnline = avail === 'online';

  return (
    <div>
      {/* Bid Sheet */}
      <BidSheet
        sheet={bidSheet}
        onClose={closeBidSheet}
        onSubmit={submitBid}
        submitting={bidSubmit}
      />

      {/* Toast notifications */}
      <div className="drv-toasts">
        {toasts.map(t => (
          <div key={t.id} className={`drv-toast drv-toast--${t.type}`}>{t.msg}</div>
        ))}
      </div>

      {/* Welcome */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:13, color:'var(--drv-text2)'}}>مرحباً</div>
        <div style={{fontSize:22, fontWeight:800}}>{driver?.full_name?.split(' ')[0] || 'سائق'} 👋</div>
      </div>

      {/* Stats */}
      <div className="drv-stats">
        <div className="drv-stat">
          <div className="drv-stat__val">{driver?.completed_orders || 0}</div>
          <div className="drv-stat__lbl">طلب منجز</div>
        </div>
        <div className="drv-stat">
          <div className="drv-stat__val" style={{fontSize:16}}><Money n={driver?.total_earnings} /></div>
          <div className="drv-stat__lbl">الأرباح</div>
        </div>
        <div className="drv-stat">
          <div className="drv-stat__val">{driver?.rating ? driver.rating.toFixed(1) : '-'}</div>
          <div className="drv-stat__lbl">التقييم ⭐</div>
        </div>
      </div>

      {/* Availability Toggle */}
      <div
        className={`drv-toggle drv-toggle--${isOnline ? 'online' : 'offline'}`}
        onClick={!toggling ? toggleAvail : undefined}
        style={{cursor: toggling ? 'wait' : 'pointer'}}
      >
        <div className="drv-toggle__info">
          <h3>
            {toggling ? '...' : isOnline ? '🟢 أنت متاح' : '🔴 أنت غير متاح'}
          </h3>
          <p>{isOnline ? 'ستصلك طلبات التوصيل' : 'لن تصلك طلبات'}</p>
        </div>
        <div className={`drv-switch ${isOnline ? 'drv-switch--on' : ''}`}/>
      </div>

      {/* Active order banner */}
      {activeOrder && (
        <div className="drv-card" style={{borderColor:'var(--drv-amber)', cursor:'pointer'}}
             onClick={() => nav('/driver/order/active')}>
          <div className="drv-card__title">طلب نشط ←</div>
          <div style={{fontWeight:700, fontSize:16, marginBottom:6}}>#{activeOrder.order_number}</div>
          <div style={{display:'flex', justifyContent:'space-between', fontSize:14}}>
            <span style={{color:'var(--drv-text2)'}}>{activeOrder.customer?.name}</span>
            <span style={{color:'var(--drv-amber)', fontWeight:600}}>
              {activeOrder.status_label || activeOrder.status}
            </span>
          </div>
          <div className="drv-btn drv-btn--amber" style={{marginTop:12}}>
            عرض الطلب ←
          </div>
        </div>
      )}

      {/* Pending Offers */}
      {offers.length > 0 && (
        <div>
          <div className="drv-section-title">🔔 طلبات جديدة ({offers.length})</div>
          {offers.map(offer => (
            <div key={offer.id} className="drv-offer">
              <div className="drv-offer__top">
                <div>
                  <div className="drv-offer__title">{offer.water_type || 'توصيل مياه'}</div>
                  <div className="drv-offer__eta">
                    🚗 {fmtMin(offer.eta)} · {offer.distance ? `${offer.distance} كم` : '-'}
                  </div>
                </div>
                <div style={{textAlign:'left'}}>
                  <div style={{fontSize:20, fontWeight:800, color:'var(--drv-green)'}}>
                    <Money n={offer.total} />
                  </div>
                  <div style={{fontSize:12, color:'var(--drv-text2)'}}>
                    موجة {offer.wave}
                  </div>
                </div>
              </div>

              <div className="drv-offer__rows">
                <div className="drv-offer__row">
                  <span>📦 الكمية</span>
                  <span>{offer.quantity} طن</span>
                </div>
                <div className="drv-offer__row">
                  <span>🚢 الشيب</span>
                  <span>{offer.ship_name} {offer.ship_city ? `— ${offer.ship_city}` : ''}</span>
                </div>
                <div className="drv-offer__row">
                  <span>📍 العنوان</span>
                  <span style={{maxWidth:180, textAlign:'left', wordBreak:'break-word'}}>{offer.address || '-'}</span>
                </div>
              </div>

              {timers[offer.id] != null && (
                <div className="drv-offer__timer">
                  ⏱ ينتهي خلال {timers[offer.id]} ثانية
                </div>
              )}

              <div className="drv-offer__btns">
                <button
                  className="drv-btn drv-btn--green"
                  disabled={bidLoading === offer.id}
                  onClick={() => openBidSheet(offer)}
                  style={{flex:2}}
                >
                  {bidLoading === offer.id
                    ? <span className="drv-spinner drv-spinner--sm"/>
                    : '💰 تقديم عرض'}
                </button>
                <button
                  className="drv-btn drv-btn--ghost"
                  onClick={() => rejectOffer(offer.id)}
                  style={{flex:1}}
                >
                  رفض
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state when online but no offers */}
      {isOnline && !activeOrder && offers.length === 0 && (
        <div className="drv-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"/>
          </svg>
          <h3>في انتظار الطلبات</h3>
          <p>أنت متاح — ستصلك إشعارات الطلبات تلقائياً</p>
        </div>
      )}

      {/* Offline state */}
      {!isOnline && (
        <div className="drv-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
          </svg>
          <h3>أنت غير متاح</h3>
          <p>فعّل زر الاستعداد لاستقبال الطلبات</p>
        </div>
      )}
    </div>
  );
}
